/**
 * The three-state resolution model (AD-1). PURE + client-safe — no network, no
 * React — so every surface (the mission-control matrix/worklist, the step-3 state
 * groups, and the stage rail's `done` map) classifies EVERY cell identically.
 * This is the ONE place the three states are computed; it replaces both the
 * tier→bucket colouring (retiring `verifyMatrix.ts`) and the direct rendering of
 * blocking/confirm escalation rows.
 *
 * Ported from the regulatory-cms studio `src/core/cellState.ts`. Adapted only at
 * the data seam: the studio built its `StateContext` from server-side SQLite
 * reads; here the context is assembled from the batched read-model endpoint
 * (`useDeclarationReadModel` — orion R2 fields keyed by public article_number)
 * plus the existing declaration hooks (statements / article-states /
 * condition-fields). The classifiers themselves are a faithful port — the same
 * inputs classify the same way — but not literally byte-identical: the studio's
 * source is TypeScript against its own row types, and the arithmetic here reads
 * the projected wire shapes. The parity that matters is asserted rather than
 * claimed: `threeStateParity.test.ts` pins it.
 *
 * Everywhere the flow classifies an (article × framework) cell — or a
 * statement's scope — it resolves to exactly one of:
 *
 *   • needs-input        (red)    a required input is entirely absent, so the
 *                                 step cannot produce.
 *   • needs-verification (yellow) the step produced an output that wants a
 *                                 human's sign-off.
 *   • confirmed          (green)  a verify mark is on record AND no red/yellow
 *                                 condition remains — the run auto-confirmed it,
 *                                 or a human signed it off.
 *   • null               (grey ring) no statement touches this cell.
 *
 * The state is a PURE DERIVED projection — never a stored status. A recompute
 * self-heals it: a node re-run changes its published output → the derived state
 * changes, with no row to supersede.
 */

import type { CoveredRef } from "../../../../api/types"
import { resolveCellVerdict } from "./cellVerdict"
import { verifiedKey } from "./conditions"
import type { Verdict } from "./studioTypes"
import { computeTier } from "./tiers"

export type CellState = "confirmed" | "needs-verification" | "needs-input"

/** Urgency rank — more-urgent wins when several statements cover one cell, and
 *  orders the worklist. needs-input (red) > needs-verification (yellow) >
 *  confirmed (green). A null (no statement) is the least urgent of all. */
const RANK: Record<CellState, number> = {
  "needs-input": 2,
  "needs-verification": 1,
  confirmed: 0,
}

export function moreUrgent(
  a: CellState | null,
  b: CellState | null,
): CellState | null {
  if (a === null) return b
  if (b === null) return a
  return RANK[a] >= RANK[b] ? a : b
}

// ── The three core classifiers — the only place the priority lives ───────────

/**
 * Classify one (framework × article) cell for step 3 (Verify statements).
 *
 * Priority (more-urgent wins), applied in order:
 *   red   — required data absent (`!requiredComplete`), even under a system
 *           verify mark (a CWC reconfirm whose values are blank is red though
 *           M8 "matches record": green needs a mark AND no red/yellow condition).
 *   green — a HUMAN sign-off is on record (it wins even over an adjudicator's
 *           risky flag — the human looked and confirmed).
 *   yellow— the adjudicator flagged the pick as risky: a system auto-mark is not
 *           enough, a human must look.
 *   yellow— a human changed a value here and has not signed it (`edited`). It
 *           OUTRANKS the system's own mark deliberately: M8 checked the values the
 *           RUN produced, and those are not the values on screen any more.
 *   yellow— the cell's published condition finding has been SOFTENED
 *           (`conditionFindingUnconfirmed`): see that input's own doc for why it
 *           sits exactly here and nowhere else.
 *   green — a system (auto) mark with nothing risky outstanding.
 *   yellow— an output exists but nobody has signed it off yet.
 */
export function classifyCell(i: {
  covered: boolean
  /** rowComplete(...) — every required input for the verdict is present. */
  requiredComplete: boolean
  /** A verify mark set by a human (not M8's system auto-mark). */
  humanVerified: boolean
  /** A verify mark set by M8's auto-verify (producer === "system"). */
  systemVerified: boolean
  /** The adjudicator flagged this cell's conflict resolution as risky (A1). */
  risky: boolean
  /** The execution record holds a HUMAN's value on this cell. Checked after
   *  `humanVerified`, so it only ever speaks about an UNSIGNED edit (§6). */
  edited?: boolean
  /**
   * THE SOFTENED CONDITION FINDING (TUR-1211, sandbox DEC-2026-0007). A
   * published M6 finding stands on this cell, but EVERY live statement covering
   * it reads a non-conditional verdict — so there is no condition datum left to
   * demand and the red state asks for something the card cannot offer and the
   * document does not name. Derived in `flowState.ts::buildStateContext`; see
   * the loop there for when a finding lands here rather than in
   * `requiredIncomplete`.
   *
   * WHERE IT SITS IS THE WHOLE RULE, both ends of it:
   *   - ABOVE `systemVerified`, so M8's own auto mark can never green a cell
   *     whose finding no human has answered. An earlier draft of this fix
   *     dropped the finding outright and was rejected in review for exactly
   *     that: the cell went green, approve passed, and nobody had looked.
   *   - BELOW `humanVerified`, because the cell's own sign-off IS the act step 3
   *     exists for — and because the framework gesture ("Verordnung als geprüft
   *     markieren") only confirms a STATEMENT that still has an open step-1
   *     issue (`useConformFramework`), which this shape does not have. Above
   *     `humanVerified` the reviewer would be left with a yellow no gesture in
   *     the mask can clear.
   * The cell's own sign-off is the ONLY act that clears it. Confirming the
   * covering statement does not: that signature is about the statement's
   * identity, and `resolveFindingVerdict` deliberately keeps it out of tier 1
   * (see the reasons there) precisely so a confirm cannot hand this cell to the
   * auto mark below.
   *
   * KNOWN LIMIT, not fixed here: a `__verified__` mark that PREDATES a newly
   * published finding absorbs it — the mark is older than the question it now
   * answers, and sitting below `humanVerified` (which the signing gesture is
   * what writes) is what makes that possible. The proper fix is to invalidate a
   * bare mark when a new unhealed finding is published for its cell, the way
   * `drop_unverifiable_marks` already does for moved values; until then the
   * recompute that publishes the finding leaves the stale signature standing.
   */
  conditionFindingUnconfirmed?: boolean
}): CellState | null {
  if (!i.covered) return null
  if (!i.requiredComplete) return "needs-input"
  if (i.humanVerified) return "confirmed"
  if (i.risky) return "needs-verification"
  if (i.edited) return "needs-verification"
  if (i.conditionFindingUnconfirmed) return "needs-verification"
  if (i.systemVerified) return "confirmed"
  return "needs-verification"
}

/**
 * Classify one statement's scope for step 2 (Apply scope) — the mirror of
 * classifyCell for the scope axis (AD-3). The step-2 CONFIRM gesture is Apply:
 *   red   — the scope resolved to no articles (nothing to apply).
 *   red   — an article in scope that the filter chain cannot explain (R-3).
 *           Invariant 2: every article in scope has a scope proof, so a missing
 *           one is a defect, never a normal state — it cannot sit under green.
 *   yellow— an article the DOCUMENT NAMES that no approved scope covers, moved onto
 *           this statement because it is its framework's broadest (D9).
 *   green — a clean scope (no unmatched refs) is auto-confirmed by the run, OR the
 *           human pressed Apply (commits match the current selection).
 *   yellow— the scope has articles but unmatched refs remain and it is not yet
 *           applied — it awaits the human's Apply.
 */
export function classifyScope(i: {
  covered: boolean
  articleCount: number
  unresolvedCount: number
  applied: boolean
  /** R-3 — articles in this statement's scope the binder could not explain (the
   *  read model's `unexplained_articles`, counted per statement). Optional for the
   *  same reason `diverged` is: a caller that has no `article_proofs` to read must
   *  say nothing rather than assert zero-with-confidence. */
  unexplainedCount?: number
  /** The reviewer's current selection differs from the last applied (or seeded)
   *  one — the concept's priority rule: an edited-but-unconfirmed value is
   *  needs-verification the instant the values diverge, OUTRANKING both green
   *  paths. Optional because the shared derivation reads persisted state only
   *  (nothing local to diverge); the live screen computes it. */
  diverged?: boolean
  /** D9 — articles the DOCUMENT NAMES that no approved scope covers, moved onto
   *  this statement because it is its framework's broadest. Yellow, not red: the
   *  run has a working answer and the human either widens the selection or says
   *  the parts are out of scope. It is checked BEFORE `applied`, because a scope
   *  applied before the gap was noticed is exactly the case to re-open — while the
   *  reds above still outrank it, no articles at all and an article the binder
   *  cannot explain both being the worse defect.
   *
   *  Nothing supplies it yet: the document's unassigned article references have no
   *  channel in this app, so every caller passes 0 and this reads as silence. */
  uncoveredNamedCount?: number
  /** The statement claims the supplier's WHOLE pool (`scope_all=true`), not a
   *  chosen subset. Exempts it from the zero-articles red: an empty pool is a
   *  fact about master data, not an unanswered question about this statement,
   *  and no gesture on the review screen could widen it. Optional, default
   *  false, so every existing caller and fixture keeps today's behaviour. */
  scopeAll?: boolean
}): CellState | null {
  if (!i.covered) return null
  if (i.articleCount === 0 && !i.scopeAll) return "needs-input"
  if ((i.unexplainedCount ?? 0) > 0) return "needs-input"
  if (i.diverged) return "needs-verification"
  if ((i.uncoveredNamedCount ?? 0) > 0) return "needs-verification"
  if (i.applied || i.unresolvedCount === 0) return "confirmed"
  return "needs-verification"
}

/**
 * Classify one statement's step-1 identity for Extract — the pre-cell mirror of
 * classifyScope. A statement establishes no compliance state without a verdict,
 * and its claim is unmeasured when it names a reference-list edition its
 * framework's catalog does not hold.
 *
 * THE IDENTITY ESCALATIONS (plan I.4 · H3, and the reference list's two cases):
 *   red   — no verdict set (the run cannot establish a compliance state).
 *   red   — the verdict is `unknown`: "the document does not allow any judgment",
 *           the CMS rubric's own words. Three ways out and the user picks — set a
 *           verdict, exclude the statement, or PROCEED AS UNKNOWN, which is the
 *           only one that reaches this classifier (`proceedAsUnknown`). The record
 *           keeps `unknown`; what changes is that the red is answered, by
 *           authorship rather than by a value.
 *   yellow— the identity on screen is neither the run's own answer nor the one a
 *           human signed (`diverged`): edited and unconfirmed (§6).
 *   yellow— the verdict is `pending`: the document DID say something (the
 *           assessment is ongoing) so the run can go on, and one sentence says
 *           what is unresolved. There is nothing to fix here, only something to
 *           agree to — so a SIGNATURE clears it, unlike every other yellow above.
 *   yellow— this framework's statements do not agree on an edition
 *           (`listConflict`). Nothing about any of their identities can be signed
 *           off until the disagreement is settled; the item is raised ONCE, at the
 *           framework, and it is settled ON these very cards.
 *   yellow— the document named an edition the catalogue does not hold
 *           (`citesUnknownEdition`). A fact about OUR content, not about the
 *           document, which is why it is told apart from silence and quoted.
 *   green — a verdict that is neither absent nor unknown, nothing unsigned, and
 *           it named no edition we cannot resolve.
 *
 * THE SILENT-EDITION YELLOW IS DELETED, and that is the whole of the change here.
 * It used to hold "the document said nothing about which list this claim was made
 * against, and the framework's edition has not been chosen" at yellow until a
 * human pressed the framework's confirm. Measured on the studio's corpus before
 * the deletion: of the enabled statements stating no edition of their own, 38
 * named nothing at all and 9 named something the catalogue does not hold. The
 * first group is SILENCE, which the run answers by itself — it reads the edition
 * the run selected, and that is not a question anyone can answer differently; the
 * second is a GAP in our content, which the run cannot answer, and it keeps its
 * own yellow above. Collapsing the two was what made the framework's confirm feel
 * load-bearing, and a yellow nobody can clear by looking is a yellow that is never
 * a default. `editionChosen` goes with the rule: it was the only thing that input
 * settled.
 */
export function classifyStatement(i: {
  covered: boolean
  hasVerdict: boolean
  frameworkHasCatalog: boolean
  /** The values on screen match neither the signature (`confirmed_values`, when a
   *  human ever confirmed) nor the run's own proposal. Derived in flowState.ts. */
  diverged?: boolean
  /** The verdict is `unknown` — no judgment is possible from the document. */
  verdictUnknown?: boolean
  /** The verdict is `pending` — the supplier says the assessment is ongoing. */
  verdictPending?: boolean
  /** A human signed this identity off (`confirmed_values` on record). What clears
   *  a `pending` claim: there is nothing to fix, only something to agree to. */
  signed?: boolean
  /** THE THIRD WAY OUT of an unknown verdict, in force. Derived, never stored: an
   *  active note of that kind AND a verdict still `unknown` — which is why setting
   *  a real verdict retires it with no gesture of its own. */
  proceedAsUnknown?: boolean
  /** This statement's FRAMEWORK has statements citing more than one reference-list
   *  edition, so which one is being read is in dispute. */
  listConflict?: boolean
  /** The document DID name a reference list for this statement and it is not one
   *  the catalogue holds — the extraction kept no id (never fabricated) but the
   *  passage it tagged survives, so this is told apart from silence and quoted. */
  citesUnknownEdition?: boolean
}): CellState | null {
  if (!i.covered) return null
  if (!i.hasVerdict) return "needs-input"
  if (i.verdictUnknown && !i.proceedAsUnknown) return "needs-input"
  if (i.diverged) return "needs-verification"
  if (i.verdictPending && !i.signed) return "needs-verification"
  if (i.listConflict) return "needs-verification"
  if (i.citesUnknownEdition && i.frameworkHasCatalog)
    return "needs-verification"
  return "confirmed"
}

/**
 * Classify one FRAMEWORK's reference-list edition — the issue unit ABOVE the
 * statement, and the only thing that lives at that unit:
 *   null  — the framework's statements agree on one reference list (or name none at
 *           all), so there is nothing to decide. Which edition a run reads is then a
 *           FACT about its statements, carried on each card.
 *   yellow— THE STATEMENTS DO NOT AGREE: they cite more than one edition, and only
 *           one can be processed per framework per document.
 *
 * Never red (there is always a working answer — the run selected one) and never
 * green: a disagreement is either live or it is not there at all.
 *
 * IT IS COUNTED OVER THE CITED EDITIONS, NOT OVER WHAT THE RUN SET ASIDE. The
 * signal used to be `editions[fw].excluded[reason=edition]` — the run's own gate
 * output — which meant the ONLY thing that could clear it was re-running the gate
 * with a different edition chosen, from a card of its own above the statements. That
 * card is gone ("no card may be unbound from a statement"), and with it the reason to
 * read the gate: the disagreement is a property of what the statements SAY, so the
 * two gestures the statements already have settle it at view time — align the lists
 * and confirm, or ignore the statement citing the edition you do not want. Both move
 * `citedLists` and neither needs the pipeline's permission.
 */
export function classifyFrameworkEdition(i: {
  /** How many DISTINCT reference lists this framework's non-ignored statements cite
   *  (a statement naming none cites nothing). See `FrameworkFacts.cited`. */
  citedLists?: number
}): "needs-verification" | null {
  return (i.citedLists ?? 0) > 1 ? "needs-verification" : null
}

/**
 * Is a statement's citation of an edition outside the catalogue no longer a gap a
 * reviewer must close?
 *
 * THE ONE PREDICATE both the `approve()` 409 gate's `MISSING_LIST_VERSION` check
 * (backend, raised BEFORE this module's worklist ever runs) and this module's own
 * `citesUnknownEdition` fact (built in `flowState.ts::buildStateContext`, which
 * THIS worklist is derived from) must agree on — a reviewer who resolves the
 * warning via `confirm_edition` or by setting a per-statement edition must never
 * pass the server gate and land back in the worklist here. Resolved when either
 * closes the gap:
 *
 *   - the FRAMEWORK's ambiguity was confirmed away — `chosen` on the edition axis,
 *     the confirmation record `confirm_edition` writes (picking IS confirming);
 *   - or THIS STATEMENT itself now carries a `listVersion` — a per-statement
 *     reading no framework-wide confirmation is needed for.
 *
 * Twin: `unknown_edition_resolved` in backend mc_declaration_cell_state.py — keep
 * in step.
 */
export function unknownEditionResolved(i: {
  chosen?: string | null
  listVersion?: string | null
}): boolean {
  if (i.chosen) return true
  return Boolean(i.listVersion)
}

// ── The derivation context — the already-published fields, normalized ────────

/** A statement's identity + scope, as steps 1/2/3 all know it. */
export interface StatementFacts {
  id: string
  framework: string
  groupIndex: number
  verdict: Verdict | null
  selectedArticles: string[]
  /** The reference-list version the statement's claim is measured against (null =
   *  unset). A FACT the card states, no longer a classifier input: silence about
   *  the edition is answered by the run (see `classifyStatement`). Still read by
   *  the green rule — `flowState.ts::statementDiverged` compares it against the
   *  signature — and by the card's own title. */
  listVersion: string | null
  /** Whether the statement's framework has a reference-list catalog at all —
   *  gates the `citesUnknownEdition` yellow, which is a gap in OUR content and is
   *  a dead end to raise where there is nothing to pick from. */
  frameworkHasCatalog: boolean
  /** A human edited this statement's verdict (the staged `verdict_override` is
   *  set). Never read by a classifier — it only tells the confirm control
   *  whether the "overridden" pill shows. */
  verdictOverridden?: boolean
  /** A human CONFIRMED this statement: the staged `confirmed_values` signature is
   *  on record. Never read by a classifier — it tells the confirm control whether
   *  green reads "Confirmed" or "Auto-confirmed" (U3). */
  confirmed?: boolean
  /** THE ONE RULE FOR GREEN (I.1) at the statement unit: the identity on screen is
   *  neither the run's own answer nor the one a human signed. Derived in
   *  `flowState.ts::statementDiverged`, never stored. */
  diverged?: boolean
  /** THE THIRD WAY OUT of an unknown verdict, in force. SERVER-DERIVED: `GET
   *  /{id}/statements` ships `proceed_unknown` per entry — an active note of that
   *  kind at `statement:<fw>:<gi>` AND an effective verdict still `unknown`. It is
   *  on the wire rather than joined in the browser precisely so both twins see the
   *  same membership, and so setting a real verdict retires it by derivation. */
  proceedUnknown?: boolean
  /** This statement's FRAMEWORK has non-ignored statements citing more than one
   *  reference list, so which one is being read is in dispute. Held identically on
   *  every statement of the framework — the DECISION is one, its stop is the
   *  framework unit, and its SURFACE is these cards. */
  listConflict?: boolean
  /** This statement is one the document tagged with an edition the catalogue does
   *  not hold (read model `editions[fw].cites_unknown_edition`). */
  citesUnknownEdition?: boolean
  /** The statement claims the supplier's WHOLE pool (`scope_all=true`), not a
   *  chosen subset. Exempts it from the zero-articles red: an empty pool is a
   *  fact about master data, not an unanswered question about this statement,
   *  and no gesture on the review screen could widen it. Optional, default
   *  false, so every existing caller and fixture keeps today's behaviour. */
  scopeAll?: boolean
}

/**
 * WHY THERE IS NO `manual` FACT HERE. The plan withholds "proceed as unknown" from
 * a HAND-ADDED statement, because the note would supersede the `included` note at
 * the same override address and lose the hand-add's own justification. That reason
 * is about the ADDRESS, not about a property of the statement — so the guard is
 * "an `included` note already stands at `statement:<fw>:<gi>`", which the persisted
 * override store answers directly (`overrideForStatement`). Nothing needed to be
 * added to the wire, and there is deliberately no invented `origin` column: this
 * app has no add-statement path at all, which is why `included` is not an
 * offerable kind in the first place.
 */

/**
 * One framework's reference-list reading, as the derived model knows it.
 *
 * It is the statements' own words, grouped — not the run's edition gate. `cited`
 * makes the framework's item both DECIDABLE and NAMEABLE from one field: its length
 * is the state (more than one ⇒ a live disagreement) and its members are what the
 * brief reads out ("this edition, cited by these statements"), which is what sends
 * the reader to the cards that can actually change it.
 *
 * `selected`, `chosen` and `setAside` are gone with the framework's own card: they
 * were the run's gate output plus its confirmation record, and nothing reads either
 * one now — the card that offered `Confirm edition` was their only consumer, and the
 * facts themselves are still on the read model for the statement cards to state.
 */
export interface FrameworkFacts {
  framework: string
  /** Per reference list this framework's NON-IGNORED statements cite, the group
   *  indexes citing it — in first-cited order, so both twins read the same list.
   *  A statement naming no edition appears nowhere here: silence is not a side of a
   *  disagreement, it is answered by the edition the run selected. */
  cited: { listVersion: string; groupIndexes: number[] }[]
}

/**
 * Everything the derivation reads. Assembled (in 2C) from the read-model
 * endpoint's orion R2 fields + the declaration's staged state. Missing pieces
 * degrade gracefully: a declaration with no `auto_verify` published (a legacy /
 * non-optimistic run) falls back to the statement verdict + the tier-1
 * auto-confirm below.
 *
 * Article-keyed maps use the PUBLIC article_number (the read-model translates the
 * pipeline's internal UUIDs at the API boundary), and the published sub-fields
 * keep their pipeline names (`article_ids`, `unresolved_refs`) — the read-model
 * hook fetches with case-conversion disabled so these arrive verbatim.
 */
/** One missing piece of a needs-input cell: the substance (cas or registry id,
 *  null when the finding names none) and the condition field keys it lacks.
 *  `anonymous`: a published finding with a null substance, which M6 writes both
 *  for a CAS-less disclosure and for a named one M3 could not connect
 *  (`fill_m6.assemble_fill`), so it identifies no disclosure. */
export interface RequiredGap {
  substance: string | null
  fields: string[]
  anonymous: boolean
}

export interface StateContext {
  statements: StatementFacts[]
  /** Guided-resolution marks: issues whose edits were committed with the
   *  recompute DEFERRED, awaiting the user's explicit "Continue" (issueId →
   *  deferred node kinds). A green issue with a pending mark stays on the
   *  worklist as "ready". Optional: surfaces that only classify cells omit it. */
  pendingIssues?: Record<string, string[]>
  /** M5 `article_verdicts`, keyed `fw:articleNumber`. */
  cellVerdicts: Record<string, { verdict: Verdict; tier?: number }>
  /** M8 `auto_verify`, keyed `fw:articleNumber`. Absent on a manual run. */
  autoVerify: Record<string, { decision?: string; producer?: string | null }>
  /** True when the run published an `auto_verify` field at all (an optimistic
   *  run). False on a manual run — enables the tier-1 auto-confirm fallback. */
  hasAutoVerify: boolean
  /** Human sign-off marks (the staged condition-fields `__verified__` rows),
   *  keyed `verifiedKey(fw, articleNumber)` = `fw|articleNumber`. */
  verified: Record<string, boolean>
  /** Cells whose required condition data is INCOMPLETE — the pipeline's published
   *  M6 `condition_fill.validation` (v3 §3's needs-input signal), keyed
   *  `fw:articleNumber`. The main-app consumes this published field rather than
   *  re-deriving completeness client-side, which avoids the CAS↔registry
   *  substance-key seam (the studio's live rowComplete path). */
  requiredIncomplete: Set<string>
  /** What each `requiredIncomplete` cell is missing, filled by the same branches
   *  that add the cell there. Display only: never read by the classifier, so the
   *  server twin has no counterpart. Optional for hand-built contexts. */
  requiredGaps?: Map<string, RequiredGap[]>
  /** Cells whose published M6 finding has been SOFTENED — every live statement
   *  covering the cell reads a non-conditional verdict, so nothing is left to
   *  fill in, but no human has decided or signed anything either. Keyed
   *  `fw:articleNumber`, derived in `flowState.ts::buildStateContext`.
   *  See `classifyCell`'s `conditionFindingUnconfirmed` input for the rule.
   *  Optional: a context built before this existed (the shared parity fixture's
   *  older cases, a caller assembling one by hand) reads as "no softened cell",
   *  which is exactly the previous behaviour. */
  conditionFindingUnconfirmed?: Set<string>
  /** On-record (prev) verdict per framework → articleNumber. */
  states: Record<string, Record<string, Verdict>>
  /** M2 `scope_resolutions`, keyed `fw:groupIndex`. `article_numbers` are the
   *  public numbers the read-model translated from the pipeline's internal ids. */
  scopeResolutions: Record<
    string,
    {
      article_numbers?: string[]
      unresolved_refs?: string[]
      covered_refs?: CoveredRef[]
    }
  >
  /** Whether a statement's scope has been APPLIED (commits match the current
   *  selection) — the step-2 confirm signal, keyed `fw:groupIndex`. */
  applied: Record<string, boolean>
  /** The reviewer's live selection differs from the last applied (or seeded) one,
   *  keyed `fw:groupIndex` — step 2's edited-and-unsigned yellow (§6).
   *
   *  Only a BROWSER can ever see one: a tick writes nothing until Apply, so the
   *  server-side twin always passes {}. Nothing supplies it here yet either —
   *  screen B owns the live selection and classifies its own card by calling
   *  `classifyScope` with the same `diverged` input directly, which is why the
   *  card is right today while the rail's shared derivation reads only persisted
   *  state. The branch lives in the shared context (and the parity fixture pins
   *  it) so the rule is ONE rule the day a live selection is shared across the
   *  surfaces rather than owned by one screen. */
  divergedScopes: Record<string, boolean>
  /** R-3 — per statement (`fw:groupIndex`), how many articles of its scope the
   *  binder's filter chain could NOT explain. Derived in `buildStateContext` from
   *  the read model's `unexplained_articles` (keyed `fw:articleNumber`, each entry
   *  naming the covering statement's `group_index`), so both twins count the same
   *  wire. Empty on a run that publishes no `article_proofs`, which is exactly the
   *  pre-binder behaviour. */
  unexplainedScope: Record<string, number>
  /** D9 — per statement (`fw:groupIndex`), how many articles the DOCUMENT NAMES
   *  that no approved scope covers and that this statement (its framework's
   *  broadest) is the one asked to widen for.
   *
   *  Always {} today, on this side and on the server's twin alike: the document's
   *  unassigned article references have no channel in this app, so nothing
   *  computes one. Required rather than optional all the same — every context
   *  states it, so the silence is a thing the builder said and not a field it
   *  forgot, and the parity fixture pins it on both sides. */
  uncoveredNamedScope: Record<string, number>
  /** Cells the adjudicator flagged risky (A1), keyed `fw:articleNumber`. */
  risky: Record<string, boolean>
  /** Cells the execution record holds a HUMAN's value on, keyed `fw:articleNumber`
   *  (the read-model's `human_edited_cells`). With the `__verified__` marks it
   *  answers "changed and not signed", which is yellow (§6). */
  humanEdited: Set<string>
  /** The reference-list reading, one entry per framework the run published an
   *  edition axis for (the read model's `editions`). Absent on a run with no
   *  published axis — a legacy row, or a framework with no catalogue — and then no
   *  framework issue exists at all, which is exactly the pre-axis behaviour. The
   *  axis is still what says WHICH frameworks have a reference list to disagree
   *  about; what they cite is read off the statements. */
  frameworks?: FrameworkFacts[]
}

export const cellKey = (framework: string, articleId: string): string =>
  `${framework}:${articleId}`
export const scopeKey = (framework: string, groupIndex: number): string =>
  `${framework}:${groupIndex}`

/** The coverage set per framework: every article any statement of that framework
 *  scopes, unioned with M2's resolved article_ids (robust to the live path where
 *  the statement's selection may lag the resolver). */
function coverageByFramework(ctx: StateContext): Map<string, Set<string>> {
  const cov = new Map<string, Set<string>>()
  const add = (fw: string, art: string) =>
    (cov.get(fw) ?? cov.set(fw, new Set()).get(fw)!).add(art)
  for (const s of ctx.statements)
    for (const a of s.selectedArticles) add(s.framework, a)
  for (const [k, res] of Object.entries(ctx.scopeResolutions)) {
    const fw = k.slice(0, k.indexOf(":"))
    for (const a of res.article_numbers ?? []) if (fw) add(fw, a)
  }
  return cov
}

/** The effective verdict of a cell: M5's per-cell verdict wins; else the most
 *  severe covering statement verdict; else null. The resolution itself lives in
 *  `cellVerdict.ts` because `flowState.ts::buildStateContext` needs the SAME
 *  precedence before any `StateContext` exists; this stays as the context-shaped
 *  entry point every caller here already uses. */
function cellVerdict(
  ctx: StateContext,
  framework: string,
  articleId: string,
): Verdict | null {
  return resolveCellVerdict(
    ctx.cellVerdicts,
    ctx.statements,
    framework,
    articleId,
  )
}

/** The inputs classifyCell needs for one cell, gathered from the context. */
export function cellInputs(
  ctx: StateContext,
  framework: string,
  articleId: string,
  covered: boolean,
): Parameters<typeof classifyCell>[0] {
  const verdict = cellVerdict(ctx, framework, articleId)
  // requiredComplete is the pipeline's published M6 validation
  // (condition_fill.validation, v3 §3): a cell with a validation finding needs
  // input. Non-conditional verdicts never carry a finding → complete.
  const requiredComplete = !ctx.requiredIncomplete.has(
    cellKey(framework, articleId),
  )
  // A human sign-off is a staged verify mark; M8's auto decision IS the system
  // verification. (The studio stores M8's auto as a verify mark distinguished by
  // producer; the main-app keeps it in the published `auto_verify` field — two
  // sources here, one there.)
  const humanVerified = ctx.verified[verifiedKey(framework, articleId)] === true
  let systemVerified =
    ctx.autoVerify[cellKey(framework, articleId)]?.decision === "auto"
  // Manual-run fallback (Seam 2): no M8 published → a tier-1 transition (compliant
  // stays compliant — nothing consequential) auto-confirms with no mark,
  // preserving the "stays compliant needs no verification" behaviour.
  if (!ctx.hasAutoVerify && !humanVerified && !systemVerified && verdict) {
    const prev = ctx.states[framework]?.[articleId] ?? null
    if (computeTier(prev, verdict) === 1) systemVerified = true
  }
  return {
    covered,
    requiredComplete,
    humanVerified,
    systemVerified,
    risky: ctx.risky[cellKey(framework, articleId)] === true,
    edited: ctx.humanEdited.has(cellKey(framework, articleId)),
    conditionFindingUnconfirmed:
      ctx.conditionFindingUnconfirmed?.has(cellKey(framework, articleId)) ===
      true,
  }
}

/** Every classified cell state, keyed `fw:articleNumber` (null cells omitted). A
 *  covered cell with no verdict yet (mid-run, before M5) is left out so the
 *  matrix shows its "determining verdict" ring rather than a premature state. */
export function deriveCellStates(ctx: StateContext): Record<string, CellState> {
  const cov = coverageByFramework(ctx)
  const out: Record<string, CellState> = {}
  for (const [fw, arts] of cov) {
    for (const art of arts) {
      if (cellVerdict(ctx, fw, art) == null) continue
      const st = classifyCell(cellInputs(ctx, fw, art, true))
      if (st) out[cellKey(fw, art)] = st
    }
  }
  return out
}

/** The state of one cell (fw × article), or null when no statement covers it. */
export function stateOfCell(
  ctx: StateContext,
  framework: string,
  articleId: string,
): CellState | null {
  const arts = coverageByFramework(ctx).get(framework)
  return classifyCell(
    cellInputs(ctx, framework, articleId, !!arts?.has(articleId)),
  )
}

/** Every statement's scope state, keyed `fw:groupIndex`. */
export function deriveScopeStates(
  ctx: StateContext,
): Record<string, CellState> {
  const out: Record<string, CellState> = {}
  for (const s of ctx.statements) {
    const st = scopeStateOf(ctx, s)
    if (st) out[scopeKey(s.framework, s.groupIndex)] = st
  }
  return out
}

export function scopeStateOf(
  ctx: StateContext,
  s: StatementFacts,
): CellState | null {
  const key = scopeKey(s.framework, s.groupIndex)
  const res = ctx.scopeResolutions[key]
  // Covered iff the statement has a resolution or a selection to reason about.
  const articleCount = res?.article_numbers?.length ?? s.selectedArticles.length
  const covered = !!res || s.selectedArticles.length > 0
  return classifyScope({
    covered,
    articleCount,
    unresolvedCount: res?.unresolved_refs?.length ?? 0,
    applied: ctx.applied[key] === true,
    unexplainedCount: ctx.unexplainedScope[key] ?? 0,
    uncoveredNamedCount: ctx.uncoveredNamedScope[key] ?? 0,
    diverged: ctx.divergedScopes[key] === true,
    scopeAll: s.scopeAll === true,
  })
}

/** The inputs `classifyStatement` needs, gathered from one statement's facts —
 *  ONE place, so the worklist, the cards and both screens cannot read the same
 *  identity differently. */
export function statementInputs(
  s: StatementFacts,
): Parameters<typeof classifyStatement>[0] {
  return {
    covered: true,
    hasVerdict: s.verdict != null,
    frameworkHasCatalog: s.frameworkHasCatalog,
    diverged: s.diverged,
    verdictUnknown: s.verdict === "unknown",
    verdictPending: s.verdict === "pending",
    signed: s.confirmed === true,
    proceedAsUnknown: s.proceedUnknown === true,
    listConflict: s.listConflict === true,
    citesUnknownEdition: s.citesUnknownEdition === true,
  }
}

/** One statement's step-1 identity state (verdict + reference list version). */
export function statementStateOf(s: StatementFacts): CellState | null {
  return classifyStatement(statementInputs(s))
}

/**
 * IS THIS STATEMENT HELD ONLY BY ITS FRAMEWORK'S EDITION CONFLICT? Then it is not
 * a stop of its own: that conflict is ONE decision, its framework's item is where
 * the decision is taken, and the statements are what that item shows. Without
 * this, a framework whose four statements disagree produces five stops for one
 * choice — one row per framework, never one per subject, broken by the unit below
 * it. The statement is still YELLOW on its card and its confirm is still blocked;
 * being held is the point, being asked four extra times is not.
 */
export function statementListConflictOnly(s: StatementFacts): boolean {
  const i = statementInputs(s)
  return (
    i.listConflict === true &&
    classifyStatement({ ...i, listConflict: false }) === "confirmed"
  )
}

/** Every statement's step-1 identity state, keyed `fw:groupIndex`. */
export function deriveStatementStates(
  ctx: StateContext,
): Record<string, CellState> {
  const out: Record<string, CellState> = {}
  for (const s of ctx.statements) {
    const st = statementStateOf(s)
    if (st) out[scopeKey(s.framework, s.groupIndex)] = st
  }
  return out
}

/** Every framework's edition-axis state, keyed by framework slug. Only the
 *  frameworks with a live disagreement appear — there is no settled reading. */
export function deriveFrameworkEditionStates(
  ctx: StateContext,
): Record<string, CellState> {
  const out: Record<string, CellState> = {}
  for (const f of ctx.frameworks ?? []) {
    const st = classifyFrameworkEdition({ citedLists: f.cited.length })
    if (st) out[f.framework] = st
  }
  return out
}

// ── Who confirmed it (U3) — derived, never stored ────────────────────────────

/**
 * THE AUTO-VS-HUMAN DISTINCTION IS DERIVED, NOT STORED (U3). Green reads
 * "✓ Auto-confirmed" when the RUN's own answer stands rather than a human's —
 * re-opening that is a meaningfully different act, so it must not look the same.
 *
 * No new field anywhere: every unit already carries the fact.
 *   cell       a staged `__verified__` mark exists → human; otherwise M8's own
 *              auto decision is what greened it → run.
 *   scope      the reviewer pressed Apply (a persisted subset) → human; a clean
 *              resolution with no unmatched refs greens itself → run.
 *   statement  a `confirmed_values` SIGNATURE is on record → human; otherwise the
 *              extraction's own verdict stands → run. (Not `verdict_override`: an
 *              override that differs from the proposal is yellow under the green
 *              rule, so a green statement carrying one is one whose reviewer typed
 *              the run's own answer back in — the run is still what settled it.)
 *
 * Only ever consulted in the green variant; a red or yellow unit has nothing to
 * say about who confirmed it.
 */
export type ConfirmedBy = "run" | "human"

export function confirmedByCell(
  ctx: StateContext,
  framework: string,
  articleId: string,
): ConfirmedBy {
  return ctx.verified[verifiedKey(framework, articleId)] === true
    ? "human"
    : "run"
}

export function confirmedByScope(
  ctx: StateContext,
  s: StatementFacts,
): ConfirmedBy {
  return ctx.applied[scopeKey(s.framework, s.groupIndex)] === true
    ? "human"
    : "run"
}

export function confirmedByStatement(s: StatementFacts): ConfirmedBy {
  return s.confirmed ? "human" : "run"
}

// ── The worklist + step completion (derived, per statement × step) ───────────

/** A worklist issue's state: open work (red/yellow), or "ready" — everything the
 *  issue asked for is green, but its guided resolution (with the deferred
 *  recompute) awaits the user's explicit Continue. */
export type WorklistState = Exclude<CellState, "confirmed"> | "ready"

/** Stable identity of a worklist issue — one issue per UNIT per step. Also the
 *  key of its pending-resolution mark. `unitId` is the statement id or
 *  `fw:<framework>`: namespaces that cannot collide (a statement id is a UUID and
 *  holds no colon), so a statement issue's key is byte-identical to what it was
 *  before the framework unit existed. */
export const issueId = (step: 1 | 2 | 3, unitId: string): string =>
  `${step}:${unitId}`

/** The unit id of a framework — the one unit above the statement. */
export const frameworkUnitId = (framework: string): string => `fw:${framework}`

/**
 * One worklist issue: one unit's open work at one step.
 *
 * The unit is the STATEMENT (the original) or a FRAMEWORK — the reference-list
 * edition is one signal per framework, never one per statement. `statementId` and
 * `groupIndex` exist only on a statement unit; `framework` on both. For step 3,
 * `articleIds` is every non-green article (or, on a ready issue, the whole scope);
 * empty otherwise.
 */
export interface WorklistIssue {
  unit: "statement" | "framework"
  /** Stable unit identity — pair with `step` for the pending-mark key, and what
   *  the URL's `&stmt=` carries (it addresses the UNIT, whatever kind it is). */
  unitId: string
  statementId?: string
  framework: string
  groupIndex?: number
  step: 1 | 2 | 3
  state: WorklistState
  articleIds: string[]
}

/**
 * The open worklist: step-1 identity issues, step-2 scope issues, and step-3
 * cell issues — ONE issue per statement per step, carrying its most urgent
 * state (an issue is done when EVERYTHING it covers is green, so red and yellow
 * articles are one body of work, not two stops). Confirmed work never enters —
 * except a confirmed issue whose pending mark awaits the user's explicit
 * resolution, which stays as "ready". Ordered step → framework → statement →
 * framework slug → group index: STRUCTURAL, never by state (see the comparator).
 */
export function deriveWorklist(ctx: StateContext): WorklistIssue[] {
  const pending = ctx.pendingIssues ?? {}
  const issues: WorklistIssue[] = []
  // ── the framework unit: the reference-list CONFLICT, at step 1 ───────────────
  // Present only while the framework's statements disagree about which edition is
  // being read; the resolved reading is null, not green, because there is no such
  // thing as a settled disagreement (see `classifyFrameworkEdition`). So the
  // "ready" branch fires on null: the conflict having gone away is exactly what
  // the reader is being told before they press Continue.
  for (const f of ctx.frameworks ?? []) {
    const unitId = frameworkUnitId(f.framework)
    const st = classifyFrameworkEdition({ citedLists: f.cited.length })
    if (st)
      issues.push({
        unit: "framework",
        unitId,
        framework: f.framework,
        step: 1,
        state: st,
        articleIds: [],
      })
    else if (pending[issueId(1, unitId)])
      issues.push({
        unit: "framework",
        unitId,
        framework: f.framework,
        step: 1,
        state: "ready",
        articleIds: [],
      })
  }
  for (const s of ctx.statements) {
    const push = (
      step: 1 | 2 | 3,
      state: WorklistState,
      articleIds: string[],
    ) =>
      issues.push({
        unit: "statement",
        unitId: s.id,
        statementId: s.id,
        framework: s.framework,
        groupIndex: s.groupIndex,
        step,
        state,
        articleIds,
      })
    // step 1 — the statement's Extract identity (verdict + reference list version)
    const stmt = statementStateOf(s)
    if (stmt && stmt !== "confirmed" && !statementListConflictOnly(s))
      push(1, stmt, [])
    else if (stmt === "confirmed" && pending[issueId(1, s.id)])
      push(1, "ready", [])
    // step 2 — the statement's scope
    const scope = scopeStateOf(ctx, s)
    if (scope && scope !== "confirmed") push(2, scope, [])
    else if (scope === "confirmed" && pending[issueId(2, s.id)])
      push(2, "ready", [])
    // step 3 — the statement's non-green articles, at their most urgent state
    if (s.verdict) {
      const open: string[] = []
      let most: CellState | null = null
      for (const art of s.selectedArticles) {
        const st = classifyCell(cellInputs(ctx, s.framework, art, true))
        if (st && st !== "confirmed") {
          open.push(art)
          most = moreUrgent(most, st)
        }
      }
      if (open.length && most && most !== "confirmed") push(3, most, open)
      else if (
        open.length === 0 &&
        s.selectedArticles.length > 0 &&
        pending[issueId(3, s.id)]
      ) {
        push(3, "ready", s.selectedArticles)
      }
    }
  }
  // Within a step, the WIDER unit first: an edition choice reframes the statements
  // under it, so answering it first is the cheaper order. Then by identity.
  //
  // THE ORDER IS STRUCTURAL, AND DELIBERATELY CARRIES NO STATE. It used to lead
  // with the issue's own state (most urgent first), which made the queue RE-SORT
  // AS WORK LANDED: the instant a stop's last item went green its state became
  // `ready`, `ready` ranked below every open state, and so the issue the user was
  // standing on jumped to the END of the queue — "Issue 1 of 3" became "Issue 3
  // of 3" under their cursor, and finishing an issue read as losing your place. A
  // position readout is only worth showing if it holds still while you work, so
  // nothing about a stop's state may decide where that stop sits. Urgency is
  // carried where it belongs: the bucket the card lives in, and the row's colour.
  const unitRank = (u: WorklistIssue["unit"]): number =>
    u === "framework" ? 0 : 1
  return issues.sort(
    (a, b) =>
      a.step - b.step ||
      unitRank(a.unit) - unitRank(b.unit) ||
      a.framework.localeCompare(b.framework) ||
      (a.groupIndex ?? -1) - (b.groupIndex ?? -1),
  )
}

/** Per-step open-work flags for the stage rail's `done` map. A step is done when
 *  it has produced work and no issue remains for it — red/yellow open work AND
 *  ready issues awaiting their explicit resolution both block. `progressed`
 *  (has the step done anything at all) stays the caller's concern. */
export interface StepBlocking {
  step1: boolean
  step2: boolean
  step3: boolean
}

export function stepBlocking(ctx: StateContext): StepBlocking {
  const wl = deriveWorklist(ctx)
  return {
    step1: wl.some((i) => i.step === 1),
    step2: wl.some((i) => i.step === 2),
    step3: wl.some((i) => i.step === 3),
  }
}
